import { create } from 'zustand';

import { api } from '../lib/api';

/**
 * Playback state (HANDOFF §9.1).
 *
 * The store holds intent and state; `PlayerHost` owns the single `<audio>`
 * element and reconciles it against this. That split is what keeps the element
 * out of the React tree's churn - see the note in `AppShell` about why the dock
 * must never be inside the router outlet.
 */

/** §6.3's list, plus `track` for a play started on the track's own page. */
export type PlaySource =
  | 'feed'
  | 'profile'
  | 'chart'
  | 'embed'
  | 'search'
  | 'radio'
  | 'collection'
  | 'track'
  | 'dashboard'
  /** A jam's shared queue; `JamWatcher` keeps the player's copy of it fresh. */
  | 'jam';

export interface PlayableTrack {
  id: number;
  title: string;
  duration_ms: number | null;
  waveform_url: string | null;
  artwork: string | null;
  /** The 200px variant, for the dock's 44px slot. */
  artwork_small?: string | null;
  owner: { username: string; display_name: string; verified?: boolean } | null;

  /**
   * The artist declared explicit content.
   *
   * Optional for the same reason `favorited` is: a track can reach the queue
   * from somewhere that did not ask for it, and an absent flag means "not
   * stated here" rather than "not explicit".
   */
  explicit?: boolean;

  /*
   * What the server said when this track was handed over, so the dock can draw
   * its heart without a request of its own.
   *
   * Optional because a track can reach the queue from somewhere that did not
   * ask - and because the favourites store's overlay is what wins anyway once
   * anything in this session has changed it. This is the starting point, not
   * the state.
   */
  favorited?: boolean;
  like_count?: number;
}

/** Off; the whole queue again from the top; this one track for ever. */
export type Repeat = 'off' | 'all' | 'one';

interface PlayerState {
  track: PlayableTrack | null;
  queue: PlayableTrack[];
  index: number;

  /*
   * Repeat and shuffle, the two things every player has and this one did
   * not. Shuffle keeps the queue as it was given - the order somebody put a
   * playlist in is worth keeping for when it is switched back off - and
   * plays through `order`, a shuffled list of indices with the current
   * track first, so nothing repeats until everything has been heard.
   */
  repeat: Repeat;
  shuffle: boolean;
  order: number[];

  playing: boolean;
  /** Seconds. Updated from `timeupdate`, so roughly four times a second. */
  position: number;
  duration: number;
  volume: number;
  muted: boolean;

  /** True between choosing a track and the first frame being decodable. */
  loading: boolean;
  error: string | null;

  /**
   * Where the audio lives, from the media session response.
   *
   * Not hardcoded, because it differs by environment: a relative `/media` path
   * in development, `https://media.<brand>` in production. Reading it from the
   * server means the client never has to know which.
   */
  mediaBase: string | null;

  /** A monotonic counter the host watches to know a fresh load is wanted. */
  loadToken: number;
  /**
   * A deliberate jump, for `PlayerHost` to carry out. Its own field rather
   * than a change in `position`, because `position` is also written by the
   * element several times a second, and a `timeupdate` that landed between
   * the seek being asked for and the element being moved put the old time
   * back, undid the seek, and in a jam was reported to everybody as a seek
   * backwards. The target rides with a token so the host moves the element
   * to what was asked, not to whatever `position` says by then.
   */
  seekRequest: { token: number; seconds: number } | null;

  /**
   * Where the current playback started from, for `plays.source` (§6.3).
   *
   * Held on the store rather than passed to the reporting call because the
   * component that knows the answer - a profile list, an album, the charts - is
   * not the one that fires the report thirty seconds later, and by then it may
   * not be mounted.
   */
  source: PlaySource;
  /**
   * Which playlist or album a `collection` play began from, so what
   * friends see says "Late Nights" rather than "a playlist". Null for
   * everything else; cleared when the source changes.
   */
  origin: { id: number; title: string } | null;

  /**
   * Somebody else gets first refusal on a play.
   *
   * Set by the jam while this person is a guest in one: a tap on a track
   * then adds it to the jam rather than playing it here, because playing
   * something else is leaving the jam's sound, and that is what Leave is
   * for. Plays *from* the jam (`source: 'jam'`) never go through it. Null
   * nearly always.
   */
  intercept: ((track: PlayableTrack, queue: PlayableTrack[] | undefined, source: PlaySource | undefined) => boolean) | null;
  /**
   * The transport is somebody else's: play, pause, skip and seek do
   * nothing while the source is the jam. A guest in a jam whose host has
   * turned guest control off. The jam's own corrections use `_seek` and
   * `_sync`, which are not locked.
   */
  locked: boolean;
  /**
   * Whether the jam's queue starts again at the end, which is the host's
   * setting rather than this listener's. Set by `JamWatcher`; ignored
   * outside a jam, where `repeat` is the personal one.
   */
  jamRepeats: boolean;

  play: (
    track: PlayableTrack,
    queue?: PlayableTrack[],
    source?: PlaySource,
    origin?: { id: number; title: string } | null,
    /**
     * Where in the queue this is, when the caller knows.
     *
     * Walking the queue does: `next`, `previous` and `ended` have the index
     * in their hands. Without it this looked the track up by id, and a queue
     * can hold the same track twice - a jam where somebody adds a song the
     * album was already going to play is the ordinary case, not a strange
     * one. The lookup then found the first copy, so stepping forward moved
     * the playhead *backwards* to it, the room reported the wrong position,
     * and everything between the two copies read as still to come.
     */
    at?: number,
  ) => Promise<void>;
  toggle: () => void;
  /** The next button: on to the next track, whatever repeat says. */
  next: () => void;
  previous: () => void;
  /** The track ran out: repeat-one starts it again, otherwise it is `next()`. */
  ended: () => void;
  cycleRepeat: () => void;
  toggleShuffle: () => void;
  seek: (seconds: number) => void;
  setVolume: (volume: number) => void;
  toggleMute: () => void;

  /** Host-only setters. Components should not call these. */
  _sync: (patch: Partial<PlayerState>) => void;
  /** `seek` without the lock, for the jam's clock corrections. */
  _seek: (seconds: number) => void;
}

const STORAGE_KEY = 'gv.player';

/**
 * What survives a reload.
 *
 * §9.1: "A refresh that loses your place feels broken." Deliberately small and
 * deliberately not a credential - the media token is an httpOnly cookie and is
 * never touched here.
 */
interface Persisted {
  trackId: number | null;
  position: number;
  volume: number;
  muted: boolean;
  queue: PlayableTrack[];
  repeat?: Repeat;
  shuffle?: boolean;
}

/** A shuffled play order with `first` at the front, so the current track stays put. */
function shuffled(length: number, first: number): number[] {
  const rest = Array.from({ length }, (_, i) => i).filter((i) => i !== first);

  for (let i = rest.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));

    [rest[i], rest[j]] = [rest[j]!, rest[i]!];
  }

  return first >= 0 && first < length ? [first, ...rest] : rest;
}

/** The queue index that comes `by` steps after `index`, honouring shuffle and repeat-all. */
/** A guest in a jam whose host holds the transport, while playing from it. */
function heldByJam(state: PlayerState): boolean {
  return state.locked && state.source === 'jam';
}

/** On to the next track, or stop at the end. What `next()` and `ended()` share. */
function advance(state: PlayerState, set: (patch: Partial<PlayerState>) => void): void {
  const target = step(state, 1);

  if (target !== null && state.queue[target]) {
    // The source travels: a step through a jam's queue is still the jam. So
    // does the position - see `play`'s `at`.
    void state.play(state.queue[target]!, state.queue, state.source, undefined, target);
  } else {
    set({ playing: false });
  }
}


function step(state: PlayerState, by: 1 | -1): number | null {
  const { queue, index, order } = state;

  if (queue.length === 0) return null;

  /*
   * A jam's queue is everybody's: it plays in the order it is written and
   * it stops at the end. Shuffle and repeat are personal settings, and a
   * listener who had either switched on walked the shared queue in their
   * own order - replaying tracks the room had already heard, reporting
   * each one, and dragging everybody back through the queue at random.
   * The phone has always forced this; the web had not.
   */
  const shuffle = state.source === 'jam' ? false : state.shuffle;
  // In a jam the room's setting decides, not this listener's.
  const repeat = state.source === 'jam' ? (state.jamRepeats ? 'all' : 'off') : state.repeat;

  const sequence = shuffle && order.length === queue.length ? order : queue.map((_, i) => i);
  const at = sequence.indexOf(index);
  let target = (at === -1 ? 0 : at) + by;

  if (target < 0 || target >= sequence.length) {
    // Any repeat wraps a manual step; only `ended()` treats `one` differently.
    if (repeat === 'off') return null;

    target = (target + sequence.length) % sequence.length;
  }

  return sequence[target] ?? null;
}

function readPersisted(): Partial<Persisted> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);

    return raw ? (JSON.parse(raw) as Partial<Persisted>) : {};
  } catch {
    // Private mode, disabled storage, or a value from an older shape. None of
    // those should stop the player from working.
    return {};
  }
}

const restored = readPersisted();

export const usePlayer = create<PlayerState>()((set, get) => ({
  track: null,
  queue: restored.queue ?? [],
  index: -1,

  playing: false,
  position: restored.position ?? 0,
  duration: 0,
  volume: restored.volume ?? 1,
  muted: restored.muted ?? false,
  repeat: restored.repeat ?? 'off',
  shuffle: restored.shuffle ?? false,
  order: [],

  loading: false,
  error: null,
  mediaBase: null,
  loadToken: 0,
  seekRequest: null,
  source: 'track',
  origin: null,
  intercept: null,
  locked: false,
  jamRepeats: false,

  play: async (track, queue, source, origin, at) => {
    /*
     * Walking the queue is not "playing something else".
     *
     * `next`, `previous` and `ended` pass the source they are already in,
     * so a step through a jam's queue reads as the jam. Without that they
     * went to the guest intercept, which put the track *into* the jam's
     * queue and played nothing: a guest's next button did nothing, and
     * every track a guest finished was added to the room's queue again.
     */
    if (source !== 'jam' && get().intercept?.(track, queue, source)) return;

    const list = queue ?? [track];
    /*
     * The position the caller gave, or the first copy of this track.
     *
     * The fallback is what every "play this" does - a click on a row knows
     * the track and not the index - and it is only wrong when the same
     * track is in the queue twice, where a caller that knows says so.
     */
    const index = at !== undefined && list[at]?.id === track.id
      ? at
      : Math.max(0, list.findIndex((t) => t.id === track.id));
    const state = get();
    // A new queue gets a new shuffle order; moving within the same one keeps it.
    const sameQueue = queue === undefined || (list.length === state.queue.length && list.every((t, i) => t.id === state.queue[i]?.id));
    // A jam's queue plays in order for everybody; shuffle is personal.
    const order = source === 'jam' || (source === undefined && state.source === 'jam')
      ? list.map((_, i) => i)
      : state.shuffle && (!sameQueue || state.order.length !== list.length)
        ? shuffled(list.length, index)
        : state.order;

    set({
      track,
      queue: list,
      index,
      order,
      loading: true,
      error: null,
      position: 0,
      duration: 0,
      // Kept when not given, so advancing through a queue does not relabel
      // every track after the first as coming from somewhere else.
      ...(source ? { source } : {}),
      // Given with a source: the pair travel together. Absent with a source:
      // the source is not a collection, and the old origin would be a lie.
      ...(source ? { origin: origin ?? null } : {}),
    });

    try {
      /*
       * A media token for the whole queue, minted before anything loads.
       *
       * One token covers every segment of every track in it, which is what lets
       * hls.js fetch segments with no per-URL rewriting - the cookie rides
       * along on its own (§4.4).
       *
       * Capped because the token travels on every segment request and cookies
       * have a practical size limit; a longer queue re-mints as it advances.
       */
      const response = await api.post<{ granted: number[]; media_base: string }>('/media/session', {
        track_ids: list.slice(0, 50).map((t) => t.id),
      });

      set((state) => ({
        mediaBase: response.media_base,
        // Bumped last, so the host sees a complete state before it loads.
        loadToken: state.loadToken + 1,
      }));
    } catch {
      set({ loading: false, playing: false, error: 'Could not start playback.' });
    }
  },

  toggle: () => {
    const { track, playing } = get();

    if (!track || heldByJam(get())) return;

    set({ playing: !playing });
  },

  next: () => {
    if (heldByJam(get())) return;

    advance(get(), set);
  },

  ended: () => {
    const state = get();

    // Repeat-one in a jam would hold the room on one track for ever.
    if (state.repeat === 'one' && state.source !== 'jam' && state.track) {
      // The same track from the top, in the same place in the queue. A reload
      // rather than a seek, so the element and the store agree it is playing.
      void state.play(state.track, state.queue, state.source, undefined, state.index);

      return;
    }

    advance(state, set);
  },

  previous: () => {
    const { queue, position } = get();

    if (heldByJam(get())) return;

    /*
     * Restart the track first, then go back.
     *
     * The convention every music player shares: "previous" more than a few
     * seconds in means "start this again", and only a second press goes back.
     * Skipping straight back is the behaviour people complain about.
     */
    if (position > 3) {
      get().seek(0);

      return;
    }

    const target = step(get(), -1);

    if (target !== null && queue[target]) {
      // The source and the position travel, as in `advance`.
      void get().play(queue[target]!, queue, get().source, undefined, target);
    } else {
      get().seek(0);
    }
  },

  cycleRepeat: () => {
    const next: Record<Repeat, Repeat> = { off: 'all', all: 'one', one: 'off' };

    set({ repeat: next[get().repeat] });
  },

  toggleShuffle: () => {
    const { shuffle, queue, index } = get();

    set({ shuffle: !shuffle, order: shuffle ? [] : shuffled(queue.length, index) });
  },

  seek: (seconds) => {
    if (heldByJam(get())) return;

    get()._seek(seconds);
  },

  _seek: (seconds) => {
    const target = Math.max(0, seconds);

    set({ position: target, seekRequest: { token: (get().seekRequest?.token ?? 0) + 1, seconds: target } });
  },

  setVolume: (volume) => set({ volume: Math.min(1, Math.max(0, volume)), muted: false }),

  toggleMute: () => set({ muted: !get().muted }),

  _sync: (patch) => set(patch),
}));

/**
 * Persist a little of the state, throttled.
 *
 * Subscribed at module load rather than from an effect, because the player
 * outlives any component and a subscription tied to one would stop the moment
 * that component happened to unmount.
 *
 * Five seconds, per §9.1. Writing on every `timeupdate` would be four
 * synchronous localStorage writes a second for the entire time somebody is
 * listening.
 */
let lastWrite = 0;

usePlayer.subscribe((state) => {
  const now = Date.now();

  if (now - lastWrite < 5000) return;

  lastWrite = now;

  try {
    const payload: Persisted = {
      trackId: state.track?.id ?? null,
      position: state.position,
      volume: state.volume,
      muted: state.muted,
      // Trimmed: the queue is for resuming, and a hundred stored tracks is a
      // large blob written every five seconds for no benefit.
      queue: state.queue.slice(0, 20),
      repeat: state.repeat,
      shuffle: state.shuffle,
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Storage full or unavailable. Losing the resume point is not worth
    // breaking playback over.
  }
});

/** The track id and offset to restore on boot, if any. */
export function restoredPlayback(): { trackId: number | null; position: number } {
  return { trackId: restored.trackId ?? null, position: restored.position ?? 0 };
}
